import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useSnapshot } from 'valtio/react'
import { isNavigationItemVisible } from '../../shared/data/courseAvailability.ts'
import { usePromise } from '../../shared/lib/hooks.ts'
import { cn } from '../../shared/lib/utils.ts'
import { AppMessageKeys } from '../../shared/translations/messages.ts'
import { urls } from '../../shared/urls.ts'
import { Icon, IconName } from '../../shared/ui/icon/icon.tsx'
import { useAppModule } from '../appContext.ts'
import { mainGreetingId } from './mainGreeting.ts'

const modules: {
  text: AppMessageKeys
  to: string
  icon: IconName
  iconTone: 'blue' | 'warm'
  textId: AppMessageKeys
}[] = [
  {
    text: 'navbar.lessons',
    to: urls.learningProfile(),
    icon: 'navStar',
    iconTone: 'blue',
    textId: 'main.lessons.subtitle',
  },
  {
    text: 'navbar.vocabPlus',
    to: urls.practice,
    icon: 'book',
    iconTone: 'warm',
    textId: 'main.vocab.subtitle',
  },
  {
    text: 'navbar.speaking',
    to: urls.speaking,
    icon: 'mic',
    iconTone: 'blue',
    textId: 'main.speaking.subtitle',
  },
  {
    text: 'navbar.testMode',
    to: urls.testMode,
    icon: 'history',
    iconTone: 'warm',
    textId: 'main.tests.subtitle',
  },
]

export function MainPage() {
  const intl = useIntl()
  const appModule = useAppModule()
  const appState = useSnapshot(appModule.appStore.state)
  const currentCourse = appModule.appStorage.get('currentCourse')
  const account = usePromise(() => appModule.chatsApi.getAccount())
  const loadedName =
    account !== 'loading' && account !== 'error' ? account.name : ''
  const name = loadedName ? loadedName : intl.formatMessage({ id: 'Friend' })
  const uiLocale = appState.uiLocale
  const courseId = currentCourse?.id
  const visibleModules = modules.filter((item) =>
    isNavigationItemVisible(item.text, courseId, uiLocale),
  )

  return (
    <div className="flex w-full flex-col gap-20 px-16 pb-100 pt-20 xl:gap-24 xl:px-0 xl:pb-40 xl:pt-28 2xl:gap-40 2xl:pb-56 2xl:pt-44">
      <header className="flex flex-col gap-8 2xl:gap-16">
        <h1 className="text-22 font-bold leading-tight text-black xl:text-30 2xl:text-44">
          <FormattedMessage id={mainGreetingId()} values={{ name }} />
        </h1>
        <p className="text-14 text-gray4 xl:text-16 2xl:text-22">
          <FormattedMessage id="main.greeting.subtitle" />
        </p>
      </header>

      <section className="flex flex-col gap-12 2xl:gap-20" aria-label="modules">
        {visibleModules.map((item) => (
          <ModuleLink
            key={item.text}
            to={item.to}
            icon={item.icon}
            iconTone={item.iconTone}
            titleId={item.text}
            textId={item.textId}
          />
        ))}
      </section>
    </div>
  )
}

function ModuleLink(props: {
  to: string
  icon: IconName
  iconTone: 'blue' | 'warm'
  titleId: AppMessageKeys
  textId: AppMessageKeys
}) {
  return (
    <Link
      to={props.to}
      className="flex items-center gap-16 rounded-18 bg-white p-18 shadow-button transition-transform hover:-translate-y-2 hover:shadow-container xl:p-22 2xl:gap-24 2xl:rounded-32 2xl:p-36"
    >
      <span
        className={cn(
          'flex size-52 shrink-0 items-center justify-center rounded-16 2xl:size-80 2xl:rounded-24',
          props.iconTone === 'blue'
            ? 'bg-second-light text-main'
            : 'bg-warning text-warning-darker',
        )}
      >
        <Icon iconName={props.icon} size="large" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-4 2xl:gap-10">
        <span className="text-18 font-bold text-black xl:text-20 2xl:text-28">
          <FormattedMessage id={props.titleId} />
        </span>
        <span className="text-14 leading-snug text-gray4 2xl:text-18">
          <FormattedMessage id={props.textId} />
        </span>
      </span>
      <span className="flex size-40 shrink-0 items-center justify-center rounded-full bg-gray-light text-black 2xl:size-56">
        <Icon iconName="arrowRight" size="small" />
      </span>
    </Link>
  )
}
